import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { LayerEntityBloc } from '@connect-services/client/blocs/layer-entity.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    NetworkPathBroadcastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { NetworkPathSelection, NetworkPathSelectionType } from '@connect-services/client/models/network-path-selection';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { FeatureService } from '@connect-services/client/services/feature.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { EntityLocation, MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapGeoJSONFeatureWithLayer } from '@connect-services/client/services/map-popup.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { NetworkPathMapService } from '@connect-services/client/services/network-path/network-path-map.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    Layer,
    LayerControllerGetEntities200ResponseAllOfItemsInner as DataEntity,
    NetworkPathDto,
    NetworkPathEdgeDto,
    NetworkPathNodeDto,
    NetworkPathPositionOptionDto,
    NetworkPathService as SdkNetworkPathService,
    PermissionAliasEnum,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Injectable({
    providedIn: 'root',
})
export class NetworkPathService implements OnDestroy {
    public readonly canAccessNetworkPath = computed<boolean>(() => {
        return this.hasPermissionsService.checkPermissions([
            PermissionAliasEnum.READ_DEPOSIT,
            PermissionAliasEnum.READ_SYNOPTIC,
        ]);
    });
    public readonly currentNetworkPath = signal<NetworkPathDto | null>(null);
    public readonly geomBboxEntity = computed<EntityLocation | null>(() => {
        return this.getEntityLocation(this.layerEntityBloc.value());
    });
    public readonly isNetworkPathLoading = signal(false);
    public readonly layer = computed<Layer | null>(() => this.layerBloc.value());

    private networkPathRequestId = 0;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly featureService = inject(FeatureService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly homeService = inject(HomeService);
    private readonly layerBloc = inject(LayerBloc);
    private readonly layerEntityBloc = inject(LayerEntityBloc);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly navigationStateService = inject(NavigationStateService);
    private readonly networkPathBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.NETWORK_PATH);
    private readonly networkPathMapService = inject(NetworkPathMapService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router = inject(Router);
    private readonly sdkNetworkPathService = inject(SdkNetworkPathService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);

    public centerMapOnEntityAndZoomIn(): void {
        const layer: Layer | null = this.layer();
        const entity: DataEntity | null = this.layerEntityBloc.value();

        if (!layer || !entity) {
            return;
        }

        this.mapDrawService.centerMapOnEntityAndZoomIn(layer.id, this.geomBboxEntity(), entity.geom);
    }

    public async entityDetailRedirection(): Promise<void> {
        const layer: Layer | null = this.layer();
        const entity: DataEntity | null = this.layerEntityBloc.value();
        const projectId: string | undefined = this.projectBloc.value()?.id;

        if (!projectId || !layer || !entity?.geom) {
            return;
        }

        const feature: MapGeoJSONFeatureWithLayer | undefined = this.mapSourceService.getRenderedEntityFeature(
            layer.id,
            entity.uuid,
            entity.geom,
        );

        const source = this.sourcesService.getSource(layer.id);

        this.centerMapOnEntityAndZoomIn();

        if (!feature || !source) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            return;
        }

        this.featureService.setData(feature);

        this.navigationStateService.markNavigated();

        const navigationSucceeded = await this.router.navigate([
            RoutePathEnum.PROJECTS,
            projectId,
            RoutePathEnum.LAYERS,
            layer.id,
            RoutePathEnum.ENTITY_DETAIL,
            entity.uuid,
        ]);

        if (!navigationSucceeded) {
            this.featureService.resetData();

            return;
        }

        this.mapHighlightEntityService.highlightMapEntity(feature, source);
    }

    public getNetworkPath(layerId: string, entityId: string, positionId?: string): Promise<NetworkPathDto> {
        const editSessionId: string | undefined = this.projectEditModeService.getEditSession()?.id;

        return firstValueFrom(
            this.sdkNetworkPathService.networkPathControllerGetNetworkPath({
                editSessionId,
                entityId,
                layerId,
                positionId,
            }),
        );
    }

    public getPositionOptions(layerId: string, entityId: string): Promise<NetworkPathPositionOptionDto[]> {
        const editSessionId: string | undefined = this.projectEditModeService.getEditSession()?.id;

        return firstValueFrom(
            this.sdkNetworkPathService.networkPathControllerGetPositionOptions({
                editSessionId,
                entityId,
                layerId,
            }),
        );
    }

    public async loadNetworkPath(selection: NetworkPathSelection): Promise<void> {
        const layer: Layer | null = this.layer();
        const entity: DataEntity | null = this.layerEntityBloc.value();

        if (!layer || !entity?.uuid) {
            return;
        }

        const positionId: string | undefined =
            selection.type === NetworkPathSelectionType.ROUTE ? selection.item.positionId : undefined;

        const requestId = ++this.networkPathRequestId;

        this.isNetworkPathLoading.set(true);

        try {
            const networkPath: NetworkPathDto = await this.getNetworkPath(layer.id, entity.uuid, positionId);

            if (requestId !== this.networkPathRequestId) {
                return;
            }

            if (
                selection.type === NetworkPathSelectionType.ROUTE &&
                !networkPath.edges.length &&
                !networkPath.nodes.length
            ) {
                await this.navigateToAllNetworkPaths();

                return;
            }

            this.setCurrentNetworkPath(networkPath);

            await this.networkPathMapService.displayNetworkPath(
                networkPath,
                selection.type === NetworkPathSelectionType.ALL,
            );
        } catch {
            if (requestId === this.networkPathRequestId) {
                this.resetCurrentNetworkPath();
                this.networkPathMapService.resetNetworkPath();
            }
        } finally {
            if (requestId === this.networkPathRequestId) {
                this.isNetworkPathLoading.set(false);
            }
        }
    }

    public async networkPathRedirection(layerId: string, entityId: string, positionId?: string): Promise<void> {
        const projectId: string | undefined = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        if (this.homeService.isExternalPage()) {
            this.broadcastChannelService.redirectNetworkPathOnHomepage(projectId, layerId, entityId, positionId);

            return;
        }

        await this.navigateToNetworkPath(projectId, layerId, entityId, positionId);
    }

    public ngOnDestroy(): void {
        this.networkPathBroadcastChannel.close();
    }

    public async onNetworkPathSelection(selection?: NetworkPathSelection): Promise<void> {
        if (!selection) {
            return;
        }

        const positionId: string =
            selection.type === NetworkPathSelectionType.ROUTE
                ? selection.item.positionId
                : NetworkPathSelectionType.ALL;

        await this.navigateToAllNetworkPaths(positionId);
    }

    public resetCurrentNetworkPath(): void {
        this.networkPathRequestId++;
        this.currentNetworkPath.set(null);

        this.networkPathBroadcastChannel.postMessage(<BroadcastChannelEvent<null>>{
            message: BroadcastChannelMessageEnum.RESET_NETWORK_PATH,
            source: BroadcastChannelSourceEnum.NETWORK_PATH,
        });
    }

    public setCurrentNetworkPath(networkPath: NetworkPathDto): void {
        this.currentNetworkPath.set(networkPath);

        this.networkPathBroadcastChannel.postMessage(<BroadcastChannelEvent<NetworkPathBroadcastChannelData>>{
            additionalData: {
                edgeUuids: networkPath.edges.map((edge: NetworkPathEdgeDto) => edge.uuid),
                nodeUuids: networkPath.nodes.map((node: NetworkPathNodeDto) => node.uuid),
            },
            message: BroadcastChannelMessageEnum.DISPLAY_NETWORK_PATH,
            source: BroadcastChannelSourceEnum.NETWORK_PATH,
        });
    }

    private getEntityLocation(entity: DataEntity | null): EntityLocation | null {
        const geom = entity?.geom;

        if (!geom?.bbox || !geom.type) {
            return null;
        }

        return {
            bbox: geom.bbox,
            type: geom.type,
        };
    }

    private async navigateToAllNetworkPaths(positionId: string = NetworkPathSelectionType.ALL): Promise<void> {
        await this.router.navigate([], {
            queryParams: { positionId },
            queryParamsHandling: 'merge',
            relativeTo: this.activatedRoute,
        });
    }

    private async navigateToNetworkPath(
        projectId: string,
        layerId: string,
        entityId: string,
        positionId?: string,
    ): Promise<void> {
        await this.router.navigate(
            [RoutePathEnum.PROJECTS, projectId, RoutePathEnum.LAYERS, layerId, RoutePathEnum.NETWORK_PATH, entityId],
            {
                queryParams: positionId ? { positionId } : undefined,
            },
        );
    }
}
